/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { BondModel, NetworkInterfaceModel } from '../models';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { NetworkEnum } from '../enums/api';
import { Observable } from 'rxjs';

/**
 * Network Service
 * Note: This class is to be used to make API calls
 *       and return with a structured response.
 */
@Injectable()
export class NetworkingApiService {
    constructor(
        private apiService: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Get all network interfaces
     * @returns {Observable<NetworkInterfaceModel[]>}
     */
    public getInterfaces(options?: any): Observable<NetworkInterfaceModel[]> {
        return new Observable(
            (observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(
                    NetworkEnum.GET_INTERFACE_ALL,
                    moduleAuth,
                    true
                )
                    .subscribe(
                        (response: NetworkInterfaceModel[]) => {
                            observer.next(response);
                            observer.complete();
                        },
                        (error: ErrorResponseModel) => {
                            observer.error(error);
                            observer.complete();
                        }
                    );
            });
    }

    /**
     * Get all network interfaces
     * @returns {Observable<NetworkInterfaceModel[]>}
     */
    public getInterfaceById(id: string, options?: any): Observable<NetworkInterfaceModel> {
        return new Observable(
            (observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService
                    .get(
                        NetworkEnum.GET_INTERFACE(id),
                        moduleAuth,
                        true
                    ).subscribe(
                    (response: NetworkInterfaceModel) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            });
    }

    /**
     * Get all network interfaces
     * @returns {Observable<NetworkInterfaceModel[]>}
     */
    public updateInterfaceById(id: string, data: NetworkInterfaceModel, options?: any): Observable<NetworkInterfaceModel> {
        return new Observable(
            (observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService
                    .put(
                        NetworkEnum.PUT_INTERFACE(id),
                        data,
                        moduleAuth,
                        true
                    ).subscribe(
                    (response: NetworkInterfaceModel) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            });
    }

    /**
     * Add a network VLAN
     * @param vlanData
     * @param options
     */
    public addVLAN(vlanData: any, options?: any): Observable<any> {
        return new Observable(
            (observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.post(
                    NetworkEnum.POST_VLAN_ADD,
                    vlanData,
                    moduleAuth,
                    true
                ).subscribe(
                    () => {
                        observer.next();
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Remove a network VLAN
     * @param vlanId
     * @param vlanData
     * @param options
     */
    public editVLAN(vlanId: string, vlanData: any, options?: any): Observable<any> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(
                    NetworkEnum.PUT_VLAN(vlanId),
                    vlanData,
                    moduleAuth,
                    true
                ).subscribe(
                    (response: any) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Remove a network VLAN
     * @param vlanId
     * @param options
     */
    public deleteVLAN(vlanId: string, options?: any): Observable<any> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.delete(
                    NetworkEnum.DELETE_VLAN(vlanId),
                    moduleAuth,
                    true
                ).subscribe(
                    (response: any) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Enable/add a bond
     * @param data
     * @param options
     */
    public addBond(data: BondModel, options?: any): Observable<any> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.post(
                    NetworkEnum.POST_BOND_ADD,
                    data,
                    moduleAuth,
                    true
                ).subscribe(
                    (response: any) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Disable/remove a bond
     * @param bondId
     * @param options
     */
    public removeBond(bondId: string, options?: any): Observable<any> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.delete(
                    NetworkEnum.DELETE_BOND(bondId),
                    moduleAuth,
                    true
                ).subscribe(
                    (response: any) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Add interface to bond.
     * @param bond
     * @param interfaceId
     * @param options
     */
    public addInterfaceToBond(bond: any, interfaceId: string, options?: any): Observable<any> {
        return new Observable(observer => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            this.apiService.put(
                NetworkEnum.PUT_BOND_INTERFACE(interfaceId),
                bond,
                moduleAuth,
                true
            ).subscribe(
                (response: any): void => {
                    observer.next(response);
                    observer.complete();
                },
                (error: ErrorResponseModel) => {
                    observer.error(error);
                    observer.complete();
                }
            );
        });
    }

    /**
     * Add interface to bond.
     * @param interfaceId
     * @param options
     */
    public removeInterfaceFromBond(interfaceId: string, options?: any): Observable<any> {
        return new Observable(observer => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            this.apiService.put(
                NetworkEnum.DELETE_BOND_INTERFACE(interfaceId),
                { type: 'ethernet' },
                moduleAuth,
                true
            ).subscribe(
                (response: any): void => {
                    observer.next(response);
                    observer.complete();
                },
                (error: ErrorResponseModel) => {
                    observer.error(error);
                    observer.complete();
                }
            );
        });
    }

    /**
     * Assign a fip group's to an interface
     * @param iface
     * @param groupID
     * @param options
     */
    public assignGroupInterface(iface: string, groupID: string, options?: any): Observable<any> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(
                    NetworkEnum.PUT_INTERFACE_GROUP(iface),
                    {
                        groupID: groupID,
                    },
                    moduleAuth,
                    true
                ).subscribe(
                    (response: any) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }
}
